import React, { useMemo, useRef, useState } from 'react';
import CustomSelect from './shared/CustomSelect';
import {
  MODES, buildBlocks, dayName, dayFull, fmtDayNum, fmtRange, fmtShort,
  TIME_LABELS, timeToLabel, labelToTime,
} from '../utils/melDate';
import { DOCKLANDS, SUBURBS, homeBaseForPostcode, roadKm, findSuburb, suburbLabel } from '../data/vicSuburbs';

/**
 * AvailabilityBuilder — inline (on-page) availability form, no modal.
 *
 * props
 *   onSubmit    (payload)   see buildPayload() for the shape
 *   today        'YYYY-MM-DD' in Australia/Melbourne (from /api/casual/access)
 *   homePostcode the casual's postcode from the session (myPostcode) — resolved
 *                to a Home Base suburb via data/vicSuburbs.js (read-only field)
 *   accent       section colour (#F97316 for Staff)
 *
 * Job Location is a CustomSelect (searchable) over every suburb within 30 km
 * of Docklands (Docklands first / default). Picking one sets each row's radius to the
 * rough road distance Home Base → Job Location; the casual can still adjust.
 *
 * Flow:  pick a range (5 buttons on the page)  →  build (one panel per week,
 *        accordion for month)  →  review  →  submit  →  back to the 5 buttons
 */

export const DEFAULT_RADIUS = 5;
const RADIUS_MIN = 1;
const RADIUS_MAX = 20;

// CustomSelect options are plain strings: "Docklands 3008", "Abbotsford 3067", …
const SUBURB_LABELS = SUBURBS.map(suburbLabel);

// slider ceiling grows if Home Base → job is further than 20 km
const radiusMax = (est) => Math.max(RADIUS_MAX, est || 0);

const emptyRow = (radius = DEFAULT_RADIUS) => ({ start: '', end: '', radius });

// Home Base → Job Location rough road km, or the default when Home Base is unknown
const estimateRadius = (homeBase, loc) => {
  const km = homeBase?.la != null && loc ? roadKm(homeBase, loc) : null;
  return km ?? DEFAULT_RADIUS;
};

const initForm = (blocks, homeBase) => {
  const radius = estimateRadius(homeBase, DOCKLANDS);
  return Object.fromEntries(
    blocks.map((b) => [b.id, {
      individual: false,
      selected: b.dates.length === 1 ? [b.dates[0]] : [],   // Today / Tomorrow auto-select
      location: DOCKLANDS,                                   // Job Location, default Docklands
      shared: emptyRow(radius),
      perDay: {},
    }]),
  );
};

/* ── validation ─────────────────────────────────────────────── */
const rowError = (row) => {
  if (!row.start || !row.end) return 'Set a start and end time';
  if (row.end <= row.start) return 'End time must be after the start';
  return null;
};

const validateBlock = (f) => {
  const errs = {};
  if (f.selected.length === 0) return { _days: 'Choose at least one day' };
  if (f.individual) {
    f.selected.forEach((iso) => {
      const e = rowError(f.perDay[iso] || emptyRow());
      if (e) errs[iso] = e;
    });
  } else {
    const e = rowError(f.shared);
    if (e) errs._shared = e;
  }
  return errs;
};

/* ── payload ────────────────────────────────────────────────── */
const locOut = (l) => (l ? { suburb: l.s, postcode: l.p, la: l.la, lo: l.lo } : null);

/* One key per build. It travels with the POST so the server can tell a
   refresh-and-resubmit of the SAME build (replay — write nothing) apart from a
   genuinely new one. reset() issues a fresh key after a successful submit. */
const newKey = () => (crypto?.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(16).slice(2)}`);

export const buildPayload = ({ mode, blocks, form, homeBase, clientKey }) => ({
  clientKey,
  mode,
  homeBase: homeBase
    ? { postcode: homeBase.postcode, suburb: homeBase.suburb, la: homeBase.la, lo: homeBase.lo }
    : null,
  createdAt: new Date().toISOString(),
  blocks: blocks
    .filter((b) => form[b.id].selected.length > 0)
    .map((b) => {
      const f = form[b.id];
      const sorted = [...f.selected].sort();
      return {
        label: b.label,
        weekStart: b.start,
        weekEnd: b.end,
        location: locOut(f.location),
        estimatedKm: homeBase?.la != null ? roadKm(homeBase, f.location) : null,
        individual: f.individual,
        slots: sorted.map((iso) => {
          const row = f.individual ? (f.perDay[iso] || f.shared) : f.shared;
          return { date: iso, day: dayName(iso), start: row.start, end: row.end, radius: Number(row.radius) };
        }),
      };
    }),
});

/* ─────────────────────────────────────────────────────────────
   Small pieces
   ───────────────────────────────────────────────────────────── */
const RadiusSlider = ({ value, onChange, homeBase, location, accent }) => {
  const est = homeBase?.la != null ? roadKm(homeBase, location) : null;
  const max = radiusMax(est);
  const pct = ((value - RADIUS_MIN) / (max - RADIUS_MIN)) * 100;
  const from = homeBase?.suburb || 'home';
  return (
    <div className="ca-radius">
      <div className="ca-radius-head">
        <span className="ca-label">Travel radius from {from}</span>
        <span className="ca-radius-value" style={{ color: accent }}>{value} km</span>
      </div>
      <input
        type="range"
        min={RADIUS_MIN}
        max={max}
        step={1}
        value={Math.min(value, max)}
        onChange={(e) => onChange(Number(e.target.value))}
        className="ca-range"
        style={{ '--fill': `${pct}%`, '--accent': accent }}
        aria-label={`Travel radius from ${from} in kilometres`}
      />
      <div className="ca-range-ticks"><span>1 km</span><span>{Math.round(max / 2)} km</span><span>{max} km</span></div>
      {est != null && (
        <div className="ca-radius-hint">
          {from} to {location?.s} is roughly <b>{est} km</b> by road
          {value !== est && (
            <button type="button" className="ca-link" onClick={() => onChange(est)}>use {est} km</button>
          )}
        </div>
      )}
    </div>
  );
};

const TimeRow = ({ row, onChange, homeBase, location, accent, title, error }) => {
  const endOptions = useMemo(
    () => (row.start ? TIME_LABELS.filter((l) => labelToTime(l) > row.start) : TIME_LABELS),
    [row.start],
  );
  return (
    <div className={`ca-row ${error ? 'ca-row-error' : ''}`}>
      {title && <div className="ca-row-title">{title}</div>}
      <div className="ca-row-times">
        <div className="ob-field">
          <CustomSelect
            label="Start"
            value={timeToLabel(row.start)}
            onChange={(label) => onChange({ ...row, start: labelToTime(label), end: row.end && row.end <= labelToTime(label) ? '' : row.end })}
            options={TIME_LABELS}
          />
        </div>
        <div className="ob-field">
          <CustomSelect
            label="End"
            value={timeToLabel(row.end)}
            onChange={(label) => onChange({ ...row, end: labelToTime(label) })}
            options={endOptions}
          />
        </div>
      </div>
      <RadiusSlider value={row.radius} onChange={(radius) => onChange({ ...row, radius })} homeBase={homeBase} location={location} accent={accent} />
      {error && <div className="ca-error">{error}</div>}
    </div>
  );
};

/* ── one block (a day, a week, or a week inside the month) ─── */
const BlockPanel = ({ block, f, onChange, homeBase, accent, errors, isMonth, isOpen, onToggle, today }) => {
  const multiDay = block.dates.length > 1;
  const location = f.location || DOCKLANDS;

  // Job Location change → new radius estimate on the shared row and every per-day row
  const setLocation = (loc) => {
    const radius = estimateRadius(homeBase, loc);
    const perDay = Object.fromEntries(Object.entries(f.perDay).map(([k, r]) => [k, { ...r, radius }]));
    onChange({ ...f, location: loc, shared: { ...f.shared, radius }, perDay });
  };

  const toggleDay = (iso) => {
    const selected = f.selected.includes(iso)
      ? f.selected.filter((d) => d !== iso)
      : [...f.selected, iso];
    const perDay = { ...f.perDay };
    if (f.individual && !perDay[iso]) perDay[iso] = { ...f.shared };
    onChange({ ...f, selected, perDay });
  };

  const toggleIndividual = (checked) => {
    const perDay = { ...f.perDay };
    if (checked) f.selected.forEach((iso) => { if (!perDay[iso]) perDay[iso] = { ...f.shared }; });
    onChange({ ...f, individual: checked, perDay });
  };

  const selectAll = () => onChange({
    ...f,
    selected: [...block.dates],
    perDay: f.individual
      ? Object.fromEntries(block.dates.map((iso) => [iso, f.perDay[iso] || { ...f.shared }]))
      : f.perDay,
  });

  const body = (
    <div className="ca-block-body">
      {/* Job Location + date range */}
      <div className="ca-meta">
        <div className="ca-meta-item ca-jobloc">
          <CustomSelect
            label="Job Availability Location"
            value={suburbLabel(location)}
            onChange={(label) => { const hit = findSuburb(label); if (hit) setLocation(hit); }}
            options={SUBURB_LABELS}
          />
        </div>
        <div className="ca-meta-item">
          <span className="ca-label">Dates</span>
          <div className="ca-daterange">{fmtRange(block.start, block.end)}</div>
        </div>
      </div>

      {/* Day chips Mon–Sun: only the days left in this range are offered */}
      <div className="ca-days-head">
        <span className="ca-label">Days available</span>
        {multiDay && (
          <button type="button" className="ca-link" onClick={selectAll}>
            {f.selected.length === block.dates.length ? 'All selected' : 'Select all'}
          </button>
        )}
      </div>
      <div className="ca-days">
        {block.dates.map((iso) => {
          const on = f.selected.includes(iso);
          return (
            <button
              key={iso}
              type="button"
              className={`ca-day ${on ? 'on' : ''} ${iso === today ? 'today' : ''}`}
              style={on ? { borderColor: accent, background: `${accent}22`, color: accent } : undefined}
              onClick={() => toggleDay(iso)}
              aria-pressed={on}
            >
              <span className="ca-day-name">{dayName(iso)}</span>
              <span className="ca-day-num">{fmtDayNum(iso)}</span>
            </button>
          );
        })}
      </div>
      {errors._days && <div className="ca-error">{errors._days}</div>}

      {/* Individual times per day */}
      {multiDay && (
        <label className="ca-check">
          <input type="checkbox" checked={f.individual} onChange={(e) => toggleIndividual(e.target.checked)} />
          <span className="ca-check-box" style={f.individual ? { background: accent, borderColor: accent } : undefined} />
          <span>Set different times for each day</span>
        </label>
      )}

      {/* Rows: one for the whole range, or one per selected day */}
      {f.individual ? (
        [...f.selected].sort().map((iso) => (
          <TimeRow
            key={iso}
            title={fmtShort(iso)}
            row={f.perDay[iso] || f.shared}
            onChange={(row) => onChange({ ...f, perDay: { ...f.perDay, [iso]: row } })}
            homeBase={homeBase}
            location={location}
            accent={accent}
            error={errors[iso]}
          />
        ))
      ) : (
        <TimeRow
          title={multiDay ? `${f.selected.length || 'No'} day${f.selected.length === 1 ? '' : 's'} selected, same times` : dayFull(block.dates[0])}
          row={f.shared}
          onChange={(shared) => onChange({ ...f, shared })}
          homeBase={homeBase}
          location={location}
          accent={accent}
          error={errors._shared}
        />
      )}
    </div>
  );

  if (!isMonth) return <div className="ca-block">{body}</div>;

  const hasErr = Object.keys(errors).length > 0;
  return (
    <div className={`ca-block ca-acc ${isOpen ? 'open' : ''} ${hasErr ? 'has-error' : ''}`}>
      <button type="button" className="ca-acc-head" onClick={onToggle} aria-expanded={isOpen}>
        <span className="ca-acc-bar" style={{ background: accent }} />
        <span className="ca-acc-title">{block.label}</span>
        <span className="ca-acc-range">{fmtRange(block.start, block.end)} · {location.s}</span>
        <span className="ca-acc-count" style={f.selected.length ? { color: accent } : undefined}>
          {f.selected.length ? `${f.selected.length} day${f.selected.length === 1 ? '' : 's'}` : 'none'}
        </span>
        <svg className="ca-acc-chev" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      <div className="ca-acc-body">{isOpen && body}</div>
    </div>
  );
};

/* ── Home Base (read-only, from the session postcode) ───────── */
const HomeBaseCard = ({ homeBase, postcode, accent }) => (
  <div className="ca-home">
    <div className="ca-home-icon" style={{ color: accent, borderColor: `${accent}55`, background: `${accent}14` }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H4a1 1 0 01-1-1V9.5z" /><path d="M9 22V12h6v10" />
      </svg>
    </div>
    <div className="ca-home-text">
      <span className="ca-label">Home Base</span>
      {homeBase ? (
        <span className="ca-home-value">
          {homeBase.suburb} {homeBase.postcode}
          {homeBase.others.length > 0 && <span className="ca-home-others"> (also {homeBase.others.join(', ')})</span>}
        </span>
      ) : (
        <span className="ca-home-value ca-home-unknown">Postcode {postcode || '—'} not found, distances use the default radius</span>
      )}
    </div>
  </div>
);

/* ── review ─────────────────────────────────────────────────── */
const Review = ({ payload, accent }) => (
  <div className="ca-review">
    {payload.homeBase && (
      <div className="ca-review-home">Home Base <b>{payload.homeBase.suburb} {payload.homeBase.postcode}</b></div>
    )}
    {payload.blocks.map((b) => (
      <div key={b.weekStart} className="ca-review-block">
        <div className="ca-review-head">
          <span className="ca-review-title">{b.label}</span>
          <span className="ca-review-range">{fmtRange(b.weekStart, b.weekEnd)}</span>
          <span className="ca-review-loc">
            {b.location?.suburb} {b.location?.postcode}
            {b.estimatedKm != null && <span className="ca-review-km"> · ~{b.estimatedKm} km</span>}
          </span>
        </div>
        {b.individual ? (
          <table className="ca-review-table">
            <thead><tr><th>Day</th><th>Start</th><th>End</th><th>Radius</th></tr></thead>
            <tbody>
              {b.slots.map((s) => (
                <tr key={s.date}>
                  <td>{fmtShort(s.date)}</td>
                  <td>{timeToLabel(s.start)}</td>
                  <td>{timeToLabel(s.end)}</td>
                  <td>{s.radius} km</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="ca-review-shared">
            <div className="ca-review-days">
              {b.slots.map((s) => (
                <span key={s.date} className="ca-review-chip" style={{ borderColor: accent, color: accent }}>
                  {s.day} {fmtDayNum(s.date)}
                </span>
              ))}
            </div>
            <div className="ca-review-line">
              {timeToLabel(b.slots[0].start)} to {timeToLabel(b.slots[0].end)}, within {b.slots[0].radius} km
            </div>
          </div>
        )}
      </div>
    ))}
  </div>
);

/* ─────────────────────────────────────────────────────────────
   Modal
   ───────────────────────────────────────────────────────────── */
const AvailabilityBuilder = ({ onSubmit, today, homePostcode = '3000', accent = '#F97316', takenDates }) => {
  const homeBase = useMemo(() => homeBaseForPostcode(homePostcode), [homePostcode]);
  const [step, setStep] = useState('mode');       // mode | build | review
  const [mode, setMode] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [form, setForm] = useState({});
  const [openBlock, setOpenBlock] = useState(null);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [clientKey, setClientKey] = useState(newKey);
  const topRef = useRef(null);

  const reset = () => {
    setStep('mode'); setMode(null); setBlocks([]); setForm({}); setOpenBlock(null); setErrors({});
    setClientKey(newKey());          // the next build is a new submission
  };

  // keep the section header in view when the step changes
  const scrollTop = () => requestAnimationFrame(() => topRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' }));

  /* One availability row per date. A range keeps working while it still has a
     free day — the days already posted are simply left out of it — and only
     greys out when every day it covers is taken. */
  const taken = takenDates || new Set();
  const freeDates = (id) => buildBlocks(id, today)
    .flatMap((b) => b.dates).filter((d) => !taken.has(d));
  const modeState = (id) => {
    const all = buildBlocks(id, today).flatMap((b) => b.dates);
    const free = all.filter((d) => !taken.has(d));
    return { total: all.length, free: free.length, full: free.length === 0 };
  };

  const pickMode = (id) => {
    const keep = new Set(freeDates(id));
    const b = buildBlocks(id, today)
      .map((x) => ({ ...x, dates: x.dates.filter((d) => keep.has(d)) }))
      .filter((x) => x.dates.length > 0);
    if (!b.length) return;                       // every day already posted
    setMode(id); setBlocks(b); setForm(initForm(b, homeBase)); setOpenBlock(b[0]?.id ?? null); setErrors({}); setStep('build'); scrollTop();
  };

  const isMonth = mode === 'month';
  const modeLabel = MODES.find((m) => m.id === mode)?.label;

  const goReview = () => {
    const next = {};
    let firstBad = null;
    blocks.forEach((b) => {
      const f = form[b.id];
      if (isMonth && f.selected.length === 0) return;       // untouched weeks are simply skipped
      const e = validateBlock(f);
      if (Object.keys(e).length) { next[b.id] = e; if (!firstBad) firstBad = b.id; }
    });
    const anySelected = blocks.some((b) => form[b.id].selected.length > 0);
    if (!anySelected) { next._global = 'Choose at least one day'; }
    setErrors(next);
    if (firstBad) { setOpenBlock(firstBad); return; }
    if (next._global) return;
    setStep('review'); scrollTop();
  };

  const payload = useMemo(
    () => (step === 'review' ? buildPayload({ mode, blocks, form, homeBase, clientKey }) : null),
    [step, mode, blocks, form, homeBase, clientKey],
  );

  const submit = async () => {
    setSubmitting(true);
    try { await onSubmit(payload); reset(); scrollTop(); } finally { setSubmitting(false); }
  };

  return (
    <section className={`ca-builder ca-step-${step}`} ref={topRef} aria-label="Create availability">
        <div className="ca-builder-head">
          <div>
            <div className="ca-builder-title">
              {step === 'mode' && 'Create availability'}
              {step === 'build' && modeLabel}
              {step === 'review' && 'Check your availability'}
            </div>
            <div className="ca-builder-sub">
              {step === 'mode' && `Melbourne date ${fmtShort(today)}`}
              {step === 'build' && (isMonth ? 'Open a week to set the job location, days and times' : 'Choose the job location, your days, times and how far you can travel')}
              {step === 'review' && 'This is what your supervisor will see'}
            </div>
          </div>
          {step !== 'mode' && (
            <button type="button" className="ca-close" onClick={() => { reset(); scrollTop(); }} aria-label="Cancel">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            </button>
          )}
        </div>

        <div className="ca-builder-body">
          {step === 'mode' && (
            <div className="ca-modes">
              {MODES.map((m, i) => {
                const st = modeState(m.id);
                return (
                  <button
                    key={m.id}
                    type="button"
                    className={`ca-mode ${st.full ? 'is-taken' : ''}`}
                    style={{ '--accent': accent, animationDelay: `${i * 40}ms` }}
                    disabled={st.full}
                    title={st.full ? 'You already have availability for every day in this range — edit it from My Availability' : undefined}
                    onClick={() => pickMode(m.id)}
                  >
                    <span className="ca-mode-label">{m.label}</span>
                    <span className="ca-mode-hint">{modeHint(m.id, today)}</span>
                    {st.full ? (
                      <span className="ca-mode-taken">Already posted</span>
                    ) : st.free < st.total ? (
                      <span className="ca-mode-free">{st.free} of {st.total} days free</span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          )}

          {step === 'build' && (
            <>
              <HomeBaseCard homeBase={homeBase} postcode={homePostcode} accent={accent} />
              {blocks.map((b) => (
                <BlockPanel
                  key={b.id}
                  block={b}
                  f={form[b.id]}
                  onChange={(nf) => setForm((prev) => ({ ...prev, [b.id]: nf }))}
                  homeBase={homeBase}
                  accent={accent}
                  errors={errors[b.id] || {}}
                  isMonth={isMonth}
                  isOpen={openBlock === b.id}
                  onToggle={() => setOpenBlock((cur) => (cur === b.id ? null : b.id))}
                  today={today}
                />
              ))}
              {errors._global && <div className="ca-error ca-error-global">{errors._global}</div>}
            </>
          )}

          {step === 'review' && payload && <Review payload={payload} accent={accent} />}
        </div>

        {step !== 'mode' && (
        <div className="ca-builder-foot">
          {step === 'build' && (
            <>
              <button type="button" className="ca-btn ghost" onClick={() => { setStep('mode'); setErrors({}); scrollTop(); }}>Back</button>
              <button type="button" className="ca-btn solid" style={{ background: accent }} onClick={goReview}>Review</button>
            </>
          )}
          {step === 'review' && (
            <>
            <button type="button" className="ca-btn ghost" onClick={() => { setStep('mode'); setErrors({}); scrollTop(); }}>Home</button>
              <button type="button" className="ca-btn ghost" onClick={() => { setStep('build'); scrollTop(); }}>Edit</button>
              <button type="button" className="ca-btn solid" style={{ background: accent }} onClick={submit} disabled={submitting}>
                {submitting ? 'Sending…' : 'Submit availability'}
              </button>
            </>
          )}
        </div>
        )}
    </section>
  );
};

/* hint under each range button, computed from the Melbourne date */
const modeHint = (id, today) => {
  const b = buildBlocks(id, today);
  if (!b.length) return '';
  if (id === 'today' || id === 'tomorrow') return fmtShort(b[0].start);
  if (id === 'month') {
    const days = b.reduce((n, w) => n + w.dates.length, 0);
    return `${b.length} week${b.length === 1 ? '' : 's'} left, ${days} days`;
  }
  return `${fmtRange(b[0].start, b[0].end)}${id === 'week' ? `, ${b[0].dates.length} days left` : ''}`;
};

export default AvailabilityBuilder;